import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories, mainSiteUrl, seoArticles } from '@/mocks/siteData';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileCatOpen(false);
    setDropdownOpen(false);
  }, [location]);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-sm border-b border-background-200/60">
      <div className="w-full px-4 md:px-10">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-4 md:gap-6">
            <Link
              to="/"
              className="font-heading text-lg md:text-2xl font-bold text-foreground-950 tracking-tight whitespace-nowrap"
            >
              oopbuyspreadsheets
            </Link>

            <div className="hidden md:flex items-center gap-1">
              <Link
                to="/"
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  isHome
                    ? 'bg-foreground-950 text-background-50'
                    : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                }`}
              >
                Home
              </Link>
              <Link
                to="/article/what-is-oopbuyspreadsheet"
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  !isHome
                    ? 'bg-foreground-950 text-background-50'
                    : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                }`}
              >
                Shopping Guide
              </Link>
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            <div className="relative hidden md:block" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-foreground-200 text-sm font-medium text-foreground-700 hover:border-foreground-400 hover:text-foreground-950 transition-colors bg-background-50 cursor-pointer whitespace-nowrap"
              >
                Categories
                {dropdownOpen ? (
                  <i className="ri-arrow-up-s-line text-xs"></i>
                ) : (
                  <i className="ri-arrow-down-s-line text-xs"></i>
                )}
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-64 bg-background-50 rounded-xl border border-background-200 py-2 max-h-80 overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors"
                      onClick={() => setDropdownOpen(false)}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex px-3 md:px-4 py-2 rounded-full bg-foreground-950 text-background-50 text-xs md:text-sm font-medium hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              oopbuy spreadsheet
            </a>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-3 md:px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs md:text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </a>

            <button
              onClick={() => {
                setMobileMenuOpen(!mobileMenuOpen);
                setMobileCatOpen(false);
              }}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-full border border-foreground-200 text-foreground-700 cursor-pointer"
            >
              {mobileMenuOpen ? (
                <i className="ri-close-line text-lg"></i>
              ) : (
                <i className="ri-menu-line text-lg"></i>
              )}
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-4 py-4">
          <div className="space-y-3">
            <Link
              to="/"
              className={`block px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isHome
                  ? 'bg-foreground-950 text-background-50'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              <i className="ri-home-line mr-2"></i>
              Home
            </Link>
            <Link
              to="/article/what-is-oopbuyspreadsheet"
              className={`block px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                !isHome
                  ? 'bg-foreground-950 text-background-50'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              <i className="ri-shopping-bag-line mr-2"></i>
              Shopping Guide
            </Link>

            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span>
                <i className="ri-price-tag-3-line mr-2"></i>
                Categories
              </span>
              {mobileCatOpen ? (
                <i className="ri-arrow-up-s-line text-sm"></i>
              ) : (
                <i className="ri-arrow-down-s-line text-sm"></i>
              )}
            </button>

            {mobileCatOpen && (
              <div className="pl-4 space-y-1 max-h-60 overflow-y-auto">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-4 py-2 rounded-lg text-sm text-foreground-600 hover:bg-background-100 hover:text-foreground-950 transition-colors"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}

            <div className="pt-3 border-t border-background-200 flex flex-col gap-2">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="block text-center px-4 py-2.5 rounded-full bg-foreground-950 text-background-50 text-sm font-medium cursor-pointer"
              >
                oopbuy spreadsheet
              </a>
            </div>

            <div className="pt-1">
              <h4 className="text-xs font-semibold uppercase tracking-widest text-foreground-400 mb-2 px-4">
                SEO Articles
              </h4>
              {seoArticles.slice(0, 5).map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="block px-4 py-2 text-sm text-foreground-600 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors"
                >
                  {article.title}
                </Link>
              ))}
              <Link
                to="/"
                className="block px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
              >
                View all articles →
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}